import { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader2, Sparkles, MessageSquare, Trash2, Plus, Menu, X, ThumbsUp, ThumbsDown, ImagePlus, FileText } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import axios from 'axios';
import { API_BASE_URL } from '../config/api.config';
import ReactMarkdown from 'react-markdown';
import Swal from 'sweetalert2';
import { useAuthStore } from '../store/authStore';

interface MessageFeedback {
  rating: 'positive' | 'negative';
  category?: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  oeCodes?: string[];
  sources?: string[];
  messageId?: string; // Backend MongoDB id — required to submit feedback
  feedback?: MessageFeedback | null;
  imageUrl?: string; // Attached image (local preview or S3 URL from history)
  fileName?: string; // Attached PDF name
}

const MAX_FILE_SIZE_MB = 20;
const SUPPORTED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
const SUPPORTED_ATTACHMENT_EXTENSIONS = [...SUPPORTED_IMAGE_EXTENSIONS, '.pdf'];
const DEFAULT_IMAGE_QUESTION = 'Please analyze the attached image and explain its content.';
const DEFAULT_PDF_QUESTION = 'Please analyze the attached PDF and explain its content.';

const isPdfFile = (file: File) =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

// crypto.randomUUID needs a secure context (HTTPS/localhost) — fall back gracefully
const genId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const FEEDBACK_CATEGORIES = [
  { value: 'incorrect_answer', label: 'Incorrect answer' },
  { value: 'incomplete_answer', label: 'Incomplete answer' },
  { value: 'did_not_answer', label: 'Did not answer the question' },
  { value: 'other', label: 'Other' },
];

interface ChatSession {
  id: string;
  title: string;
  messages: Message[];
  createdAt: Date;
  updatedAt: Date;
  isNew?: boolean; // Track if session is not yet saved to backend
}

export function Chatbot() {
  const { accessToken, user } = useAuthStore();
  const initialSessionId = 'temp-' + Date.now();

  // Check if user has delete permission
  const canDelete = user?.permissions?.includes('ai_chatbot_delete') || false;
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([
    {
      id: initialSessionId,
      title: 'New Chat',
      messages: [
        {
          id: '1',
          role: 'assistant',
          content: 'Hello! I\'m your AI assistant. How can I help you today?',
          timestamp: new Date(),
        },
      ],
      createdAt: new Date(),
      updatedAt: new Date(),
      isNew: true, // Mark as new (not yet in backend)
    },
  ]);
  const [currentSessionId, setCurrentSessionId] = useState(initialSessionId);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentSession = chatSessions.find(s => s.id === currentSessionId);
  const messages = currentSession?.messages || [];

  // Get axios config with authorization header
  const getAxiosConfig = () => ({
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Load chat sessions from backend on mount (only if authenticated)
  useEffect(() => {
    if (accessToken) {
      loadSessionsFromBackend();
    } else {
      console.warn('[Chatbot] No access token found, skipping session load');
    }
  }, [accessToken]);

  const loadSessionsFromBackend = async () => {
    try {
      if (!accessToken) {
        console.warn('[Chatbot] Cannot load sessions: No access token');
        return;
      }
      console.log('[Chatbot] Loading sessions from backend...');
      const response = await axios.get(`${API_BASE_URL}/sessions`, getAxiosConfig());
      console.log('[Chatbot] Backend response:', response.data);

      if (response.data.success && response.data.data.sessions.length > 0) {
        const sessions = response.data.data.sessions.map((s: any) => {
          // Deduplicate messages based on content + role + timestamp
          const seenMessages = new Set<string>();
          const uniqueMessages = s.messages.filter((m: any) => {
            const key = `${m.role}-${m.content}-${m.timestamp}`;
            if (seenMessages.has(key)) {
              console.warn('[Chatbot] Duplicate message detected and removed:', key.substring(0, 50));
              return false;
            }
            seenMessages.add(key);
            return true;
          });

          return {
            id: s.session_id,
            title: s.title || 'New Chat',
            messages: uniqueMessages.map((m: any, mIndex: number) => ({
              // Generate unique ID using session_id + message index + timestamp
              id: `${s.session_id}-msg-${mIndex}-${new Date(m.timestamp).getTime()}`,
              role: m.role,
              content: m.content,
              timestamp: new Date(m.timestamp),
              oeCodes: m.oeCodes || [],
              sources: m.sources || [],
              messageId: m.message_id || undefined,
              feedback: m.feedback || null,
              imageUrl: m.imageUrl || undefined,
              fileName: m.fileName || undefined
            })),
            createdAt: new Date(s.created_at),
            updatedAt: new Date(s.updated_at),
            isNew: false // These are from backend, not new
          };
        });
        console.log('[Chatbot] Loaded sessions:', sessions.length);

        // Log messages for debugging
        sessions.forEach((s: ChatSession) => {
          console.log(`[Chatbot] Session ${s.id}: ${s.messages.length} messages`);
        });

        setChatSessions(sessions);
        setCurrentSessionId(sessions[0].id);
      } else {
        console.log('[Chatbot] No sessions found in backend, keeping default');
      }
    } catch (error) {
      console.error('[Chatbot] Error loading sessions from backend:', error);
      // If no sessions exist or error, keep default new session
    }
  };

  const createNewChat = () => {
    const newSession: ChatSession = {
      id: 'temp-' + Date.now(),
      title: 'New Chat',
      messages: [
        {
          id: '1',
          role: 'assistant',
          content: 'Hello! I\'m your AI assistant. How can I help you today?',
          timestamp: new Date(),
        },
      ],
      createdAt: new Date(),
      updatedAt: new Date(),
      isNew: true, // Mark as new
    };
    setChatSessions(prev => [newSession, ...prev]);
    setCurrentSessionId(newSession.id);
  };

  const deleteChat = async (sessionId: string) => {
    if (chatSessions.length === 1) {
      Swal.fire({
        title: 'Cannot Delete',
        text: 'You must have at least one chat session.',
        icon: 'info',
        confirmButtonColor: '#1e73be',
      });
      return;
    }

    const sessionToDelete = chatSessions.find(s => s.id === sessionId);

    const result = await Swal.fire({
      title: 'Delete this chat?',
      text: 'This chat and all its messages will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    try {
      // Only delete from backend if it's not a new session
      if (sessionToDelete && !sessionToDelete.isNew) {
        await axios.delete(`${API_BASE_URL}/sessions/${sessionId}`, getAxiosConfig());
      }

      // Update local state
      setChatSessions(prev => prev.filter(s => s.id !== sessionId));
      if (currentSessionId === sessionId) {
        const remainingSessions = chatSessions.filter(s => s.id !== sessionId);
        setCurrentSessionId(remainingSessions[0]?.id || 'temp-' + Date.now());
      }

      // Show success toast
      Swal.fire({
        title: 'Deleted!',
        text: 'Chat deleted successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error deleting session:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to delete chat session.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    }
  };

  const deleteAllChats = async () => {
    const result = await Swal.fire({
      title: 'Delete All Chat History?',
      text: 'This will permanently delete all your chat sessions. This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete all!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      // Show loading
      Swal.fire({
        title: 'Deleting...',
        text: 'Please wait while we delete all sessions',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Delete all from backend
      await axios.delete(`${API_BASE_URL}/sessions`, getAxiosConfig());

      // Reset to default new session
      const newSessionId = 'temp-' + Date.now();
      setChatSessions([
        {
          id: newSessionId,
          title: 'New Chat',
          messages: [
            {
              id: '1',
              role: 'assistant',
              content: 'Hello! I\'m your AI assistant. How can I help you today?',
              timestamp: new Date(),
            },
          ],
          createdAt: new Date(),
          updatedAt: new Date(),
          isNew: true,
        },
      ]);
      setCurrentSessionId(newSessionId);

      // Show success
      Swal.fire({
        title: 'Deleted!',
        text: 'All chat history has been deleted successfully.',
        icon: 'success',
        confirmButtonColor: '#1e73be',
      });
    } catch (error) {
      console.error('Error deleting all sessions:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to delete all chat sessions. Please try again.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    }
  };

  const updateSessionTitle = (sessionId: string, firstMessage: string) => {
    const title = firstMessage.slice(0, 30) + (firstMessage.length > 30 ? '...' : '');
    setChatSessions(prev => prev.map(s =>
      s.id === sessionId ? { ...s, title, updatedAt: new Date() } : s
    ));
  };

  const attachFile = (file: File) => {
    const lowerName = file.name.toLowerCase();
    const isSupported = SUPPORTED_ATTACHMENT_EXTENSIONS.some(ext => lowerName.endsWith(ext));
    if (!isSupported) {
      Swal.fire({
        icon: 'warning',
        title: 'Unsupported Format',
        text: 'Accepted formats: JPG, PNG, HEIC, WebP, PDF.',
        confirmButtonColor: '#1e73be',
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      Swal.fire({
        icon: 'warning',
        title: 'File Too Large',
        text: `Maximum file size is ${MAX_FILE_SIZE_MB}MB.`,
        confirmButtonColor: '#1e73be',
      });
      return;
    }

    if (filePreview) URL.revokeObjectURL(filePreview);
    setSelectedFile(file);
    // PDFs get a file chip instead of an image thumbnail
    setFilePreview(isPdfFile(file) ? null : URL.createObjectURL(file));
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (file) attachFile(file);
  };

  const clearSelectedFile = () => {
    if (filePreview) URL.revokeObjectURL(filePreview);
    setSelectedFile(null);
    setFilePreview(null);
  };

  // Drag & drop — attach by dropping a file anywhere on the chat area
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) attachFile(file);
  };

  const handleSendMessage = async () => {
    if ((!input.trim() && !selectedFile) || isLoading || !currentSession) return;

    if (!accessToken) {
      Swal.fire({
        icon: 'warning',
        title: 'Authentication Required',
        text: 'Please login to use the chatbot.',
      });
      return;
    }

    const fileToSend = selectedFile;
    const previewUrl = filePreview;
    const sendingPdf = fileToSend ? isPdfFile(fileToSend) : false;

    const userMessage: Message = {
      id: genId(),
      role: 'user',
      content: input.trim() || (fileToSend ? (sendingPdf ? DEFAULT_PDF_QUESTION : DEFAULT_IMAGE_QUESTION) : ''),
      timestamp: new Date(),
      imageUrl: !sendingPdf ? previewUrl || undefined : undefined,
      fileName: sendingPdf && fileToSend ? fileToSend.name : undefined,
    };

    // Reset the attachment UI immediately
    setSelectedFile(null);
    setFilePreview(null);

    // Update current session with user message
    setChatSessions(prev => prev.map(s =>
      s.id === currentSessionId
        ? { ...s, messages: [...s.messages, userMessage], updatedAt: new Date() }
        : s
    ));

    // Update title if this is the first user message
    if (currentSession.messages.length === 1) {
      updateSessionTitle(currentSessionId, userMessage.content);
    }

    setInput('');
    setIsLoading(true);

    try {
      let response;

      if (fileToSend) {
        // Multipart request with image/PDF attachment (NCHAT-RQ-17)
        const formData = new FormData();
        if (input.trim()) formData.append('question', input.trim());
        if (currentSession && !currentSession.isNew) {
          formData.append('session_id', currentSessionId);
        }
        formData.append('file', fileToSend);

        response = await axios.post(`${API_BASE_URL}/chatbot/ask`, formData, getAxiosConfig());
      } else {
        // Call chatbot API - only send session_id if it's not a new session
        const requestBody: any = {
          question: userMessage.content
        };

        // Only include session_id if the session already exists in backend
        if (currentSession && !currentSession.isNew) {
          requestBody.session_id = currentSessionId;
        }

        response = await axios.post(`${API_BASE_URL}/chatbot/ask`, requestBody, getAxiosConfig());
      }

      if (response.data.success) {
        const assistantMessage: Message = {
          id: genId(),
          role: 'assistant',
          content: response.data.data.answer,
          timestamp: new Date(),
          oeCodes: response.data.data.oeCodes || [],
          sources: response.data.data.sources || [],
          messageId: response.data.data.message_id || undefined,
          feedback: null,
        };

        // Get session_id from backend response
        const backendSessionId = response.data.data.session_id;

        // If this was a new session, update it with backend session_id
        if (currentSession?.isNew && backendSessionId) {
          setCurrentSessionId(backendSessionId);
          setChatSessions(prev => prev.map(s =>
            s.id === currentSessionId
              ? {
                  ...s,
                  id: backendSessionId,
                  messages: [...s.messages, assistantMessage],
                  updatedAt: new Date(),
                  isNew: false // No longer new, now synced with backend
                }
              : s
          ));
        } else {
          // Existing session, just update messages
          setChatSessions(prev => prev.map(s =>
            s.id === currentSessionId
              ? { ...s, messages: [...s.messages, assistantMessage], updatedAt: new Date() }
              : s
          ));
        }
      } else {
        throw new Error(response.data.error || 'Failed to get response');
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: genId(),
        role: 'assistant',
        content: error.response?.data?.error || 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      };
      setChatSessions(prev => prev.map(s =>
        s.id === currentSessionId
          ? { ...s, messages: [...s.messages, errorMessage], updatedAt: new Date() }
          : s
      ));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const applyFeedbackToState = (messageId: string, feedback: MessageFeedback) => {
    setChatSessions(prev => prev.map(s =>
      s.id === currentSessionId
        ? {
            ...s,
            messages: s.messages.map(m =>
              m.messageId === messageId ? { ...m, feedback } : m
            ),
          }
        : s
    ));
  };

  const sendFeedback = async (
    messageId: string,
    rating: 'positive' | 'negative',
    category?: string,
    comment?: string
  ) => {
    const body: any = { message_id: messageId, rating };
    if (category) body.category = category;
    if (comment) body.comment = comment;
    await axios.post(`${API_BASE_URL}/chatbot/feedback`, body, getAxiosConfig());
    applyFeedbackToState(messageId, { rating, category });
  };

  const handleFeedback = async (message: Message, rating: 'positive' | 'negative') => {
    if (!message.messageId) return;

    try {
      // Record the rating immediately — details below are optional
      await sendFeedback(message.messageId, rating);

      if (rating === 'negative') {
        const categoriesHtml = FEEDBACK_CATEGORIES.map(c =>
          `<label style="display:block;margin-bottom:8px;cursor:pointer;font-size:14px;text-align:left">
            <input type="radio" name="fb-category" value="${c.value}" style="margin-right:8px">${c.label}
          </label>`
        ).join('');

        const { value } = await Swal.fire({
          title: 'What was wrong?',
          html: `<div style="padding:0 8px">${categoriesHtml}
            <textarea id="fb-comment" class="swal2-textarea" style="margin:12px 0 0;width:calc(100% - 16px)" placeholder="Additional details (optional)"></textarea>
          </div>`,
          showCancelButton: true,
          confirmButtonText: 'Submit',
          cancelButtonText: 'Skip',
          confirmButtonColor: '#1e73be',
          cancelButtonColor: '#6b7280',
          preConfirm: () => {
            const selected = document.querySelector<HTMLInputElement>('input[name="fb-category"]:checked');
            const comment = (document.getElementById('fb-comment') as HTMLTextAreaElement | null)?.value?.trim();
            return { category: selected?.value, comment };
          },
        });

        if (value && (value.category || value.comment)) {
          await sendFeedback(message.messageId, 'negative', value.category, value.comment);
        }
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to save feedback. Please try again.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    }
  };

  return (
    // translate="no" + notranslate: browser page-translation (Chrome/Edge) rewrites
    // React-managed text nodes and crashes reconciliation with insertBefore errors
    <div className="h-[calc(100vh-4rem)] flex flex-col notranslate" translate="no">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <PageHeader
            title="AI Chatbot"
            description="Ask questions and get instant AI-powered answers"
            breadcrumbs={[{ label: 'Chatbot' }]}
          />
        </div>
        {/* Upload Content button - Hidden */}
        {/* <button
          onClick={() => navigate('/sharepoint-sync?mode=rag')}
          className="px-6 py-3 bg-gradient-to-r from-[#ea7500] to-[#f59e42] hover:from-[#ea7500]/90 hover:to-[#f59e42]/90 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 hover:-translate-y-0.5"
        >
          <Upload className="w-5 h-5" />
          Upload Content
        </button> */}
      </div>

      {/* Main Container with Sidebar */}
      <div className="flex-1 flex gap-4 overflow-hidden mt-6">
        {/* Sidebar - Chat History */}
        <div
          className={`${
            sidebarOpen ? 'w-80' : 'w-0'
          } transition-all duration-300 flex-shrink-0 overflow-hidden`}
        >
          <div className="h-full bg-white rounded-2xl shadow-xl border-2 border-gray-100 flex flex-col">
            {/* Sidebar Header */}
            <div className="p-4 border-b border-gray-200">
              <button
                onClick={createNewChat}
                className="w-full px-4 py-3 bg-gradient-to-r from-[#1e73be] to-[#4A90C8] hover:from-[#1e73be]/90 hover:to-[#4A90C8]/90 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                New Chat
              </button>
            </div>

            {/* Chat History List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {chatSessions.map((session) => (
                <div
                  key={session.id}
                  className={`group relative p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                    currentSessionId === session.id
                      ? 'bg-gradient-to-r from-[#1e73be]/10 to-[#4A90C8]/10 border-2 border-[#1e73be]/30'
                      : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                  }`}
                  onClick={() => setCurrentSessionId(session.id)}
                >
                  <div className="flex items-start gap-3">
                    <MessageSquare
                      className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                        currentSessionId === session.id ? 'text-[#1e73be]' : 'text-gray-400'
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-800 truncate">
                        {session.title}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        {session.messages.length} messages
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {session.updatedAt.toLocaleDateString()}
                      </p>
                    </div>
                    {canDelete && chatSessions.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteChat(session.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Sidebar Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-2">
              <p className="text-xs text-gray-500 text-center">
                {chatSessions.length} chat{chatSessions.length !== 1 ? 's' : ''} total
              </p>
              {canDelete && (
                <button
                  onClick={deleteAllChats}
                  className="w-full px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-semibold transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Clear All History
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Chat Container */}
        <div
          className="flex-1 flex flex-col bg-white rounded-2xl shadow-xl border-2 border-gray-100 overflow-hidden relative"
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
        {/* Drag & drop overlay */}
        {isDragging && (
          <div className="absolute inset-0 z-50 bg-[#1e73be]/10 border-4 border-dashed border-[#1e73be] rounded-2xl flex items-center justify-center pointer-events-none">
            <div className="bg-white px-6 py-4 rounded-2xl shadow-xl flex items-center gap-3">
              <ImagePlus className="w-6 h-6 text-[#1e73be]" />
              <span className="text-sm font-semibold text-gray-700">Drop an image or PDF here</span>
            </div>
          </div>
        )}
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gradient-to-br from-gray-50 to-blue-50/20">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex items-start gap-4 animate-in ${
                message.role === 'user' ? 'flex-row-reverse' : ''
              }`}
            >
              {/* Avatar */}
              <div
                className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${
                  message.role === 'user'
                    ? 'bg-gradient-to-br from-[#1e73be] to-[#4A90C8]'
                    : 'bg-gradient-to-br from-[#ea7500] to-[#f59e42]'
                }`}
              >
                {message.role === 'user' ? (
                  <User className="w-5 h-5 text-white" />
                ) : (
                  <Bot className="w-5 h-5 text-white" />
                )}
              </div>

              {/* Message Bubble */}
              <div
                className={`flex-1 max-w-3xl ${
                  message.role === 'user' ? 'text-right' : 'text-left'
                }`}
              >
                <div
                  className={`inline-block px-5 py-3 rounded-2xl shadow-md ${
                    message.role === 'user'
                      ? 'bg-gradient-to-br from-[#1e73be] to-[#4A90C8] text-white'
                      : 'bg-white text-gray-800 border border-gray-200'
                  }`}
                >
                  {message.role === 'user' ? (
                    <div>
                      {message.fileName && (
                        <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg bg-white/15 border border-white/30">
                          <FileText className="w-4 h-4 text-white flex-shrink-0" />
                          <span className="text-xs text-white truncate max-w-[220px]">{message.fileName}</span>
                        </div>
                      )}
                      {message.imageUrl && (
                        <img
                          src={message.imageUrl}
                          alt="Attached"
                          className="max-w-[240px] max-h-[240px] rounded-lg mb-2 border border-white/30"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                        {message.content}
                      </p>
                    </div>
                  ) : (
                    <div className="text-sm leading-relaxed prose prose-sm max-w-none">
                      <ReactMarkdown
                        components={{
                          h1: ({ node, ...props }) => <h1 className="text-xl font-bold mt-4 mb-2" {...props} />,
                          h2: ({ node, ...props }) => <h2 className="text-lg font-bold mt-3 mb-2" {...props} />,
                          h3: ({ node, ...props }) => <h3 className="text-base font-bold mt-2 mb-1" {...props} />,
                          p: ({ node, ...props }) => <p className="mb-2" {...props} />,
                          ul: ({ node, ...props }) => <ul className="list-disc ml-4 mb-2" {...props} />,
                          ol: ({ node, ...props }) => <ol className="list-decimal ml-4 mb-2" {...props} />,
                          li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                          strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
                          em: ({ node, ...props }) => <em className="italic" {...props} />,
                          code: ({ node, inline, ...props }: any) =>
                            inline ? (
                              <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono" {...props} />
                            ) : (
                              <code className="block bg-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto" {...props} />
                            ),
                          blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-gray-300 pl-4 italic my-2" {...props} />,
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>

                      {/* Display OE Codes if available */}
                      {message.oeCodes && message.oeCodes.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-gray-200">
                          <p className="text-xs font-semibold text-gray-600 mb-2">📚 Related OE Codes:</p>
                          <div className="flex flex-wrap gap-2">
                            {message.oeCodes.map((code, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2.5 py-1 rounded-lg bg-blue-50 border border-blue-200 text-xs font-medium text-blue-700"
                              >
                                {code}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Display Sources if available */}
                      {message.sources && message.sources.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs font-semibold text-gray-600 mb-2">📄 Sources:</p>
                          <div className="flex flex-wrap gap-2">
                            {message.sources.map((source, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2.5 py-1 rounded-lg bg-green-50 border border-green-200 text-xs font-medium text-green-700"
                              >
                                {source}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Answer feedback (NCHAT-RQ-18) */}
                      {message.messageId && (
                        <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-2">
                          <p className="text-xs text-gray-500 mr-1">Was this helpful?</p>
                          <button
                            onClick={() => handleFeedback(message, 'positive')}
                            title="Helpful"
                            className={`p-1.5 rounded-lg border transition-all ${
                              message.feedback?.rating === 'positive'
                                ? 'bg-green-100 border-green-300 text-green-600'
                                : 'bg-white border-gray-200 text-gray-400 hover:text-green-600 hover:border-green-300'
                            }`}
                          >
                            <ThumbsUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleFeedback(message, 'negative')}
                            title="Not helpful"
                            className={`p-1.5 rounded-lg border transition-all ${
                              message.feedback?.rating === 'negative'
                                ? 'bg-red-100 border-red-300 text-red-600'
                                : 'bg-white border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-300'
                            }`}
                          >
                            <ThumbsDown className="w-4 h-4" />
                          </button>
                          {message.feedback && (
                            <span className="text-xs text-gray-400 ml-1">Thanks for your feedback!</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-2 px-2">
                  {message.timestamp.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </div>
          ))}

          {/* Loading Indicator */}
          {isLoading && (
            <div className="flex items-start gap-4 animate-in">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg bg-gradient-to-br from-[#ea7500] to-[#f59e42]">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 max-w-3xl">
                <div className="inline-block px-5 py-3 rounded-2xl shadow-md bg-white border border-gray-200">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-[#ea7500] animate-spin" />
                    <span className="text-sm text-gray-600">Thinking...</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-200 bg-white p-4">
          <div className="max-w-4xl mx-auto">
            {/* Attachment preview (NCHAT-RQ-17) */}
            {selectedFile && (
              <div className="mb-3 inline-flex items-center gap-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-xl">
                {filePreview ? (
                  <img
                    src={filePreview}
                    alt="Preview"
                    className="w-12 h-12 object-cover rounded-lg"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <FileText className="w-8 h-8 text-[#1e73be] flex-shrink-0" />
                )}
                <span className="text-xs text-gray-700 max-w-[200px] truncate">{selectedFile.name}</span>
                <button
                  onClick={clearSelectedFile}
                  className="p-1 rounded-lg hover:bg-blue-100 text-gray-500 hover:text-red-500 transition-colors"
                  title="Remove attachment"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <div className="flex items-end gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf"
                onChange={handleFileInputChange}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
                title="Attach image or PDF (JPG, PNG, HEIC, WebP, PDF)"
                className="p-3.5 rounded-2xl border-2 border-gray-200 text-gray-500 hover:text-[#1e73be] hover:border-[#1e73be] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ImagePlus className="w-5 h-5" />
              </button>
              <div className="flex-1 relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message..."
                  disabled={isLoading}
                  className="w-full px-5 py-3.5 pr-12 rounded-2xl border-2 border-gray-200 focus:border-[#1e73be] focus:ring-4 focus:ring-[#1e73be]/10 outline-none transition-all duration-200 text-sm bg-gray-50 focus:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <Sparkles className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
              <button
                onClick={handleSendMessage}
                disabled={(!input.trim() && !selectedFile) || isLoading}
                className="px-6 py-3.5 bg-gradient-to-r from-[#1e73be] to-[#4A90C8] hover:from-[#1e73be]/90 hover:to-[#4A90C8]/90 text-white rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg hover:-translate-y-0.5"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-3 text-center">
              Press Enter to send • Shift+Enter for new line • Attach or drag & drop an image/PDF (JPG, PNG, HEIC, WebP, PDF)
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default Chatbot;
